import React, { useContext, useEffect, useState } from 'react'
import ReactStars from 'react-rating-stars-component'
import { captureGAEvent, raiseToast } from '../../utils/helper'
import { isEmpty } from 'lodash'
import useAxios from 'axios-hooks'
import { GET_REVIEWS_API } from '../../utils/apiUrls'
import { AppContext } from '../../context/app'
import { withTheme } from 'theming'
import { useTranslation } from 'react-i18next'

const ReviewModal = (props) => {
  const { type, id, handleClose, title, onReviewed, alreadyReviewed, theme } = props
  const [reviewTitle, setReviewTitle] = useState('')
  const [reviewDesc, setReviewDesc] = useState('')
  const [reviewRating, setReviewRating] = useState(0)
  const [isEdit, setIsEdit] = useState(false)
  const { setLoader, isCustomTemplate, userObj } = useContext(AppContext)
  const { t } = useTranslation()

  // Animation state
  const [show, setShow] = useState(false)

  // Show modal with delay on mount
  useEffect(() => {
    setTimeout(() => setShow(true), 10)
  }, [])

  useEffect(() => {
    if (!isEmpty(alreadyReviewed)) {
      setReviewTitle(alreadyReviewed.title)
      setReviewDesc(alreadyReviewed.description)
      setReviewRating(alreadyReviewed.rating)
      setIsEdit(true)
    }
  }, [alreadyReviewed])

  const [{ data: createReview, loading: createReviewLoading }, postReview] = useAxios(
    {
      url: GET_REVIEWS_API + '/' + type + '/' + id,
      method: 'post',
    },
    { manual: true },
  )

  const [{ data: updateReview, loading: updateReviewLoading }, updateReviewCall] = useAxios(
    {
      url: GET_REVIEWS_API + '?review_id=' + alreadyReviewed._id,
      method: 'put',
    },
    { manual: true },
  )

  useEffect(() => {
    setLoader(createReviewLoading || updateReviewLoading)
  }, [createReviewLoading, updateReviewLoading])

  const handleCloseWithAnimation = () => {
    setShow(false)
    setTimeout(() => {
      handleClose()
    }, 400)
  }

  useEffect(() => {
    if (createReview && createReview.success) {
      raiseToast('success', t('review_success'))
      onReviewed()
      handleCloseWithAnimation()
    }
  }, [createReview])

  useEffect(() => {
    if (updateReview && updateReview.success) {
      raiseToast('success', t('review_update_success'))
      onReviewed()
      handleCloseWithAnimation()
    }
  }, [updateReview])

  const onClickSubmit = () => {
    if (reviewRating < 0.5) {
      raiseToast('error', t('provide_rating'))
    } else if (!isEmpty(reviewDesc) && isEmpty(reviewTitle)) {
      raiseToast('error', t('provide_review_title'))
    } else {
      if (isEdit) {
        updateReviewCall({
          data: {
            title: reviewTitle,
            description: reviewDesc,
            rating: reviewRating,
          },
        })
      } else {
        captureGAEvent('review', 'review', 'Review', {
          content_id: id,
          user_id: userObj?._id || '',
          user_email: userObj?.email_id || '',
        })
        postReview({
          data: {
            title: reviewTitle,
            description: reviewDesc,
            rating: reviewRating,
          },
        })
      }
    }
  }

  return (
    <>
      <div
        style={{
          backgroundColor: isCustomTemplate ? `${theme.c1}90` : 'rgb(17 ,24, 39,0.9)',
          opacity: show ? 1 : 0,
          transition: 'opacity 0.4s ease',
        }}
        className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none"
      >
        <div
          style={{
            transform: show ? 'scale(1)' : 'scale(0.9)',
            opacity: show ? 1 : 0,
            transition: 'all 0.4s ease',
          }}
          className={`relative w-11/12  xl:w-5/12 md:w-2/4 my-2 mx-auto`}
        >
          <div
            style={{
              background: isCustomTemplate ? theme.c10 : '#151c26',
              fontFamily: 'Arial',
              boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.8), 0 6px 20px 0 rgba(0, 0, 0, 0.60)',
              border: '',
            }}
            className="shadow-lg relative flex flex-col md:w-full p-5 "
          >
            <div className=" py-2 object-right-top">
              <button
                className="p-1 float-right xl:text-3xl leading-none font-semibold"
                onClick={handleCloseWithAnimation}
              >
                <span
                  style={{ color: isCustomTemplate ? theme.c4 : '#fff' }}
                  className="  h-6 w-6 text-xl xl:text-2xl block outline-none focus:outline-none"
                >
                  ×
                </span>
              </button>
              <div className="flex flex-col justify-start">
                <h4
                  style={{ color: isCustomTemplate ? theme.c4 : '#fff' }}
                  className=" text-sm md:text-xl  font-semibold"
                >
                  {isEdit ? t('edit_review_for') : t('write_review_for')}
                  {' ' + title}
                </h4>
              </div>
            </div>
            <div>
              <div className="mt-6">
                <p style={{ color: isCustomTemplate ? theme.c4 : '#fff' }} className={'text-white'}>
                  {t('rating') + ' *'}
                </p>
                <ReactStars
                  count={5}
                  size={24}
                  activeColor={isCustomTemplate ? theme.c2 : '#ffd700'}
                  value={!isEmpty(alreadyReviewed) ? alreadyReviewed.rating : reviewRating}
                  edit={true}
                  onChange={(val) => setReviewRating(val)}
                  isHalf={true}
                />
              </div>

              <div className="mt-6">
                <p
                  style={{ color: isCustomTemplate ? theme.c4 : '#fff' }}
                  className={'text-white mb-2'}
                >
                  {t('review_heading')}
                </p>
                <input
                  type="text"
                  value={reviewTitle}
                  placeholder={t('please_provide_review')}
                  onChange={(e) => setReviewTitle(e.target.value)}
                  style={{
                    backgroundColor: isCustomTemplate ? theme.c1 : 'rgb(17,24,39)',
                    borderColor: isCustomTemplate ? theme.c5 : 'rgb(75,85,99)',
                    color: isCustomTemplate ? theme.c4 : '#fff',
                  }}
                  className="bg-gray-900 border border-gray-600 text-white lg:text-lg rounded block w-full
                  py-1 p-2.5 pl-2 focus:outline-none"
                />
              </div>

              <div className="mt-6">
                <p
                  style={{ color: isCustomTemplate ? theme.c4 : '#fff' }}
                  className={'text-white mb-2'}
                >
                  {t('review_description')}
                </p>
                <textarea
                  rows="3"
                  value={reviewDesc}
                  placeholder={t('review_brief')}
                  onChange={(e) => setReviewDesc(e.target.value)}
                  style={{
                    backgroundColor: isCustomTemplate ? theme.c1 : 'rgb(17,24,39)',
                    borderColor: isCustomTemplate ? theme.c5 : 'rgb(75,85,99)',
                    color: isCustomTemplate ? theme.c4 : '#fff',
                  }}
                  className="bg-gray-900 border border-gray-600 text-white lg:text-lg rounded block w-full py-1 p-2.5 pl-2 focus:outline-none"
                />
              </div>
              <div className={'flex justify-end'}>
                <button
                  className="px-4 py-1 focus:outline-none ml-5 mt-10"
                  style={{
                    backgroundColor: isCustomTemplate ? theme.c5 : '#1976d2',
                    color: isCustomTemplate ? theme.c7 : '#fff',
                    borderRadius: '6px',
                  }}
                  onClick={onClickSubmit}
                >
                  {t('submit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default withTheme(ReviewModal)
